import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { v4 as uuid } from 'uuid';
import { Observable } from 'rxjs';

import { TooltipOptions } from '@fcom/ui-components/components/tooltip/tooltip.interface';
import { TooltipPosition } from '@fcom/ui-components/components/tooltip/tooltip-position.enum';

import { ButtonTheme } from '../buttons/button';
import { ModalButtons } from '../modal';
import { ContentSpaceClass, NotificationTheme } from '../../components/notifications';
import { IconName } from '../../components/icons';

/**
 * A expandable area controllable via inputs.
 *
 * This only handles the expandable area and not the button to trigger this content area. This
 * should be done in a case-by-case basis.
 *
 * @example
 *
 * <code>
 *   <pre>
 *       <fcom-filters
          [hasFiltering]="hasFiltering"
          [numberToShowWhenFiltered]="number"
          [headerText]="headerText"
          [buttonText]="buttonText"
          [buttonTextWhenFiltered]="'Filtered'"
          (primaryFooterButtonClick)="showResults()"
          (secondaryFooterButtonClick)="showResults()"
 *       >
 *          <div>
 *            <p>Can have some static and non-interactable content...</p>
 *            <fcom-checkbox [checked]="checked" (change)="changeHandledInParentComponent()">
 *               <span class="font-body-1">...but I understand that the point is to have interaction here</span>
 *            </fcom-checkbox>
 *          </div>
 *       </fcom-filters>
 *   </pre>
 * </code>
 */
@Component({
  selector: 'fcom-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FiltersComponent {
  readonly ButtonTheme = ButtonTheme;
  readonly ModalButtons = ModalButtons;
  readonly NotificationTheme = NotificationTheme;
  readonly IconName = IconName;
  readonly NotificationSpaceClass = ContentSpaceClass;

  expandContainerId = uuid();
  expandButtonId = uuid();

  @Input()
  disabled = false;

  @Input()
  buttonText: string;

  @Input()
  buttonTextWhenFiltered: string;

  @Input()
  headerText: string;

  @Input()
  hasFiltering = false;

  @Input()
  numberToShowWhenFiltered?: number;

  @Input()
  primaryFooterButtonTitle: string;

  @Input()
  modalTitle: string;

  @Input()
  notificationContent: string;

  @Input()
  isModalOpen: boolean;

  @Input()
  dispayNotification: boolean;

  @Input()
  isMobile$: Observable<boolean>;

  @Output()
  primaryFooterButtonClick: EventEmitter<void> = new EventEmitter();

  @Input()
  secondaryFooterButtonTitle: string;

  @Output()
  secondaryFooterButtonClick: EventEmitter<void> = new EventEmitter();

  @Output()
  tooltipOpeningClick: EventEmitter<void> = new EventEmitter();

  @Output()
  showModal = new EventEmitter();

  @Output()
  closeModal = new EventEmitter();

  tooltipOptions: TooltipOptions = {
    tooltipID: `filters-tooltip-${this.expandButtonId}`,
    openByDefault: false,
    enableOnClick: true,
    closeOnScroll: true,
    showArrowCaret: false,
    showLeftBorder: false,
    tooltipPosition: TooltipPosition.BOTTOM,
    roundedCorners: true,
    hasPadding: false,
  };
}
